import React from 'react';
import {
  Box, Paper, Typography, Button, Grid, TextField, MenuItem,
  Autocomplete, CircularProgress, IconButton, Select, FormControl, InputLabel, Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  Badge as BadgeIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import ClienteInfoCard from '../ClienteInfoCard';
import { MemoriaFacturacionAlert } from '../MemoriaFacturacionAlert';

/**
 * Paper "Información General": selección de cliente/paciente, lista de
 * precios, tipo de comprobante, observaciones y datos fiscales asociados.
 */
const AlbaranCabeceraForm = ({
  esEditable,
  esNuevo,
  albaran,
  cabeceraForm,
  setCabeceraForm,
  selectedPaciente,
  pacienteOptions,
  loadingPacienteBusqueda,
  onSearchPaciente,
  onSeleccionarPaciente,
  onAbrirBuscarPaciente,
  listasPrecio,
  tipoDte,
  setTipoDte,
  mostrarInfoCliente,
  setMostrarInfoCliente,
  onAbrirConsumo,
  onUsarDatosAnteriores,
}) => {
  const theme = useTheme();
  const idCliente = selectedPaciente?.id_cliente || selectedPaciente?.cliente_id;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
      }}
    >
      {/* Header unificado al estilo de AlbaranDetalleTable */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.common.black, 0.01),
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 0.8,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
            }}
          >
            <InfoIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Información General
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={esEditable ? 'Modo Edición' : 'Lectura'}
            color={esEditable ? 'primary' : 'default'}
            variant="outlined"
            sx={{ fontWeight: 600, mr: 1 }}
          />

          {selectedPaciente && idCliente && (
            <Button
              variant={mostrarInfoCliente ? 'contained' : 'outlined'}
              color="info"
              size="small"
              startIcon={<BadgeIcon />}
              onClick={() => setMostrarInfoCliente(!mostrarInfoCliente)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: mostrarInfoCliente ? `0 4px 12px ${alpha(theme.palette.info.main, 0.2)}` : 'none',
              }}
            >
              {mostrarInfoCliente ? 'Ocultar Receptor' : 'Ver Datos Receptor'}
            </Button>
          )}

          {selectedPaciente && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<AssessmentIcon />}
              onClick={onAbrirConsumo}
              size="small"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Consumo Histórico
            </Button>
          )}
        </Box>
      </Box>

      {/* Cuerpo del formulario con el pad unificado */}
      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={2}>
          {/* Selector de Cliente / Paciente */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete
                fullWidth
                disabled={!esEditable}
                options={pacienteOptions}
                value={selectedPaciente}
                getOptionLabel={(opt) =>
                  typeof opt === 'string'
                    ? opt
                    : `${opt.nombre_cliente || ''} - ${opt.nombre_paciente || ''}${
                        opt.nombre_raza ? ` (${opt.nombre_raza})` : ''
                      }`
                }
                isOptionEqualToValue={(opt, val) => opt.id_paciente === val?.id_paciente}
                onInputChange={(_, value) => onSearchPaciente(value)}
                onChange={(_, value) => onSeleccionarPaciente(value)}
                loading={loadingPacienteBusqueda}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente / Paciente"
                    placeholder="Escriba nombre o teléfono..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPacienteBusqueda ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {esEditable && (
                <IconButton
                  color="primary"
                  onClick={onAbrirBuscarPaciente}
                  sx={{
                    height: 56,
                    width: 56,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                  title="Búsqueda avanzada de paciente"
                >
                  <SearchIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Lista de Precios */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              select
              fullWidth
              disabled={!esEditable}
              label="Lista de Precios"
              value={
                listasPrecio.some(
                  (lp) => String(lp.id_lista_precio) === String(esNuevo ? cabeceraForm.id_lista_precio : albaran.id_lista_precio)
                )
                  ? (esNuevo ? cabeceraForm.id_lista_precio : albaran.id_lista_precio)
                  : ''
              }
              onChange={(e) => setCabeceraForm({ ...cabeceraForm, id_lista_precio: e.target.value })}
            >
              {listasPrecio.map((lp) => (
                <MenuItem key={lp.id_lista_precio} value={lp.id_lista_precio}>
                  {lp.nombre_lista}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Tipo de Comprobante */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth disabled={!esEditable}>
              <InputLabel id="tipo-dte-label">Tipo de Comprobante</InputLabel>
              <Select
                labelId="tipo-dte-label"
                value={tipoDte}
                label="Tipo de Comprobante"
                onChange={(e) => setTipoDte(e.target.value)}
              >
                <MenuItem value="01">Factura Consumidor Final (01)</MenuItem>
                <MenuItem value="03">Comprobante Crédito Fiscal (03)</MenuItem>
                <MenuItem value="11">Factura de Exportación (11)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Observaciones */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              disabled={!esEditable}
              label="Observaciones"
              placeholder="Notas generales..."
              value={esNuevo ? cabeceraForm.observaciones : albaran.observaciones || ''}
              onChange={(e) => setCabeceraForm({ ...cabeceraForm, observaciones: e.target.value })}
            />
          </Grid>

          {/* Alertas de Memoria de Facturación */}
          {selectedPaciente && (idCliente || selectedPaciente.id_paciente) && (
            <Grid item xs={12}>
              <MemoriaFacturacionAlert
                idCliente={idCliente}
                idPaciente={selectedPaciente.id_paciente}
                onUsarDatosAnteriores={onUsarDatosAnteriores}
              />
            </Grid>
          )}

          {/* Tarjeta con Info Detallada del Cliente */}
          {mostrarInfoCliente && selectedPaciente && idCliente && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <ClienteInfoCard idCliente={idCliente} readOnly={!esEditable} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Paper>
  );
};

export default AlbaranCabeceraForm;